import React, { useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";

import clsx from "clsx";
import { any, filter, findIndex, isEmpty, map, propEq } from "ramda";
import { capitalize } from "../../../util/stringFormat";
import { getType } from "../../../util/mediaUtil";
import { faCircleMinus } from "@fortawesome/pro-solid-svg-icons";
import { useDeleteModuleItems } from "../moduleServices";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { notBlank } from "../../../util/ext";
import { useRemoveContentFromCollection } from "../../collections/collectionServices";

import Checkbox from "@material-ui/core/Checkbox";
import IconButton from "@material-ui/core/IconButton";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";

import ContentCard from "../../content/browse/ContentCard";
import ContentOriginCard from "../../common/ContentOriginCard";
import ContentParentsCard from "../../organize/content-groups/card/ContentParentsCard";
import FontAwesomeIcon from "../../../components/FontAwesomeIcon";
import { useSelected } from "../../organize/OrganizeProvider";

const useStyles = makeStyles(theme => ({
  checkbox: {
    color: "inherit",
  },

  checkboxSelected: {
    color: "white",
  },

  contentCell: {
    paddingRight: theme.spacing(3),
  },

  contentTypeCell: {
    textTransform: "capitalize",
  },

  dragging: {
    opacity: 0.5,
  },

  icon: {
    color: theme.palette.primary.darker,
  },

  mediaTitle: {
    color: theme.palette.brandPrimary,
    fontWeight: "bold",
  },

  root: {
    "&$selected": {
      backgroundColor: theme.palette.primary.main,
      "&> *, &> p": {
        fontWeight: theme.typography.fontWeightMedium,
      },
    },

    "&$selected:hover": {
      backgroundColor: theme.palette.primary.main,
      "&> *, &> p": {
        fontWeight: theme.typography.fontWeightMedium,
      },
    },
  },

  reviewTitle: {
    paddingLeft: theme.spacing(6),
  },

  selected: {},
}));

// const ActionMenu = () => {
//   const [anchorEl, setAnchorEl] = useState(null);
//   const handleMenuClick = event => {
//     event.stopPropagation();
//     event.preventDefault();
//     setAnchorEl(event.currentTarget);
//   };

//   const handleClose = event => {
//     event.stopPropagation();
//     setAnchorEl(null);
//   };
//   return (
//     <Fragment>
//       <IconButton onClick={handleMenuClick}>
//         <FontAwesomeIcon icon={faEllipsisVertical} />
//       </IconButton>
//       <Menu
//         id={`other-actions-for-anthology`}
//         anchorEl={anchorEl}
//         open={Boolean(anchorEl)}
//         onClose={handleClose}
//         getContentAnchorEl={null}
//         anchorOrigin={{
//           vertical: "top",
//           horizontal: "left",
//         }}
//         transformOrigin={{
//           vertical: "top",
//           horizontal: "right",
//         }}
//       >
//         <MenuItem onClick={e => e.stopPropagation()}>
//           Remove From Collection in this Anthology
//         </MenuItem>
//         <MenuItem onClick={e => e.stopPropagation()}>
//           Add to Collection in this Anthology
//         </MenuItem>
//       </Menu>
//     </Fragment>
//   );
// };

const ModuleFilesRow = props => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const { selectedContent, updateSelectedContent } = useSelected();

  const {
    content,
    handleOpenViewerClick,
    loading,
    module,
    setLoading,
    searchState,
    sourceFilter,
    showAlert,
    showConfirm,
  } = props;

  const handleContentEditClick = useCallback(
    event => {
      event.stopPropagation();
      const { pathname, search } = location;
      const fromPath = notBlank(search) ? pathname + search : pathname;
      history.push(`/creator/content/${content.id}`, {
        from: fromPath,
        searchState,
      });
    },
    [content.id, searchState, location, history],
  );

  const handleViewerClick = event => {
    event.stopPropagation();
    handleOpenViewerClick(content);
  };

  const deleteModuleItems = useDeleteModuleItems();
  const removeContentFromCollection = useRemoveContentFromCollection();
  const handleRemoveFromModule = useCallback(
    async event => {
      event.stopPropagation();
      event.preventDefault();
      if (notBlank(id)) {
        try {
          setLoading(true);

          // if module is not found on parentModules,
          // then check parentCollections for all collections that might be included in module
          if (findIndex(propEq("id", id), content.parentModules) === -1) {
            const collectionParents = filter(collection => {
              return (
                findIndex(mod => mod.id === id, collection.ancestorModules) !==
                -1
              );
            }, content.parentCollections);
            showConfirm(
              <Typography variant="h2">
                Removing{" "}
                <span className={classes.mediaTitle}>{content.title}</span> from{" "}
                <span className={classes.mediaTitle}>{module.name}</span> will
                remove it from the following collections:
              </Typography>,
              <div>
                {collectionParents.map(cp => (
                  <p key={content.id}>{cp.name}</p>
                ))}
              </div>,
              {
                onConfirm: async () => {
                  setLoading(true);
                  await Promise.all(
                    map(
                      collection =>
                        removeContentFromCollection.mutateAsync({
                          collectionId: collection.id,
                          contentIds: [content.id],
                        }),
                      collectionParents,
                    ),
                  );
                  setLoading(false);
                },
              },
              "Remove",
            );
          } else {
            await deleteModuleItems.mutateAsync({
              id: id,
              ids: [content.id],
            });
          }

          setLoading(false);
        } catch (err) {
          showAlert("error", [err.message]);
        }
      }
    },
    [
      id,
      classes.mediaTitle,
      content,
      deleteModuleItems,
      module.name,
      removeContentFromCollection,
      showAlert,
      showConfirm,
      setLoading,
    ],
  );

  return (
    <TableRow
      id={content.id}
      classes={{
        root: clsx(classes.root),
      }}
      onClick={() => updateSelectedContent(content)}
      hover
      tabIndex={-1}
      role="checkbox"
      aria-checked={any(propEq("id", content.id), selectedContent)}
      selected={any(propEq("id", content.id), selectedContent)}
    >
      <TableCell padding="checkbox" align="center">
        <Checkbox
          color="primary"
          className={clsx(classes.checkbox, {
            [classes.selectedCheckbox]: any(
              propEq("id", content.id),
              selectedContent,
            ),
          })}
          checked={any(propEq("id", content.id), selectedContent)}
          inputProps={{
            "aria-labelledby": "content-checkbox-" + content.id,
          }}
        />
      </TableCell>

      <TableCell
        key={"title:" + content.id}
        align="left"
        className={classes.contentCell}
      >
        <ContentCard
          handleThumbnailClick={handleViewerClick}
          handleTitleClick={handleContentEditClick}
          loading={loading}
          content={content}
          selected={any(propEq("id", content.id), selectedContent)}
        />
      </TableCell>

      <TableCell key={"date-updated:" + content.id} align="left">
        {new Date(content.auditUpdated).toLocaleDateString()}
      </TableCell>

      <TableCell
        key={"content-type:" + content.id}
        align="left"
        className={classes.contentTypeCell}
      >
        {capitalize(getType(content))}
      </TableCell>

      <TableCell
        onClick={e => e.stopPropagation()}
        key={"origin:" + content.id}
        align="left"
      >
        <ContentOriginCard content={content} sourceFilter={sourceFilter} />
      </TableCell>

      <TableCell key={"included-in:" + content.id} align="left">
        <ContentParentsCard content={content} setLoading={setLoading} />
      </TableCell>

      <TableCell key={"actions: " + content.id} align="center">
        <Tooltip title="Remove">
          <span>
            <IconButton
              onClick={handleRemoveFromModule}
              disabled={!isEmpty(selectedContent)}
            >
              <FontAwesomeIcon
                icon={faCircleMinus}
                variant="delete"
                disabled={!isEmpty(selectedContent)}
              />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

export default ModuleFilesRow;
